import { BarChart3, BookOpen, Building2, FileText, LayoutDashboard, ListChecks, Plus, Settings, Sparkles, Timer, Wallet } from "lucide-react";
import { useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import type { User } from "wasp/entities";
import useTranslation from "../../client/hooks/useTranslation";
import { Button } from "../../client/components/ui/button";
import NotificationsBell from "../../notifications/NotificationsBell";
import { SidebarBottomSection } from "../../client/components/AppSidebar/SidebarBottomSection";
import { LiteDataProvider } from "../data/LiteDataProvider";
import { LiteActionsProvider, useLiteActions } from "./LiteActions";
import { LiteIntroModal } from "./LiteIntroModal";
import { LiteOnboarding } from "../onboarding/LiteOnboarding";

/** `also` : autres chemins qui allument l'entrée (ex. /playbook pour EdgyAI, /session/:id pour Sessions). */
type NavItem = { to: string; label: string; short: string; icon: typeof Wallet; end?: boolean; also?: string[] };

export function Logo() {
  return (
    <span className="font-logo font-bold text-xl tracking-tight text-foreground">
      edgely<span className="text-[#00C9A7]">.</span>
    </span>
  );
}

/** Shell du mode Lite : une seule barre latérale, avec entrées de navigation, et la barre mobile. */
export function LiteShell({ user, banner, children }: { user: Partial<User> & { isAdmin?: boolean }; banner?: ReactNode; children: ReactNode }) {
  return (
    <LiteDataProvider>
      <LiteActionsProvider>
        <ShellInner user={user} banner={banner}>{children}</ShellInner>
      </LiteActionsProvider>
    </LiteDataProvider>
  );
}

function ShellInner({ user, banner, children }: { user: Partial<User> & { isAdmin?: boolean }; banner?: ReactNode; children: ReactNode }) {
  const t = useTranslation().lite;
  const { openAddTrade } = useLiteActions();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const nav: NavItem[] = [
    { to: "/dashboard", label: t.nav.dashboard, short: t.nav.dashboardShort, icon: LayoutDashboard, end: true },
    { to: "/prop-firm-manager", label: t.nav.propFirms, short: t.nav.propFirmsShort, icon: Building2 },
    // "/account/" (barre finale) = détail d'un compte uniquement : "/account" seul est la page Réglages.
    { to: "/trading-accounts", label: t.nav.accounts, short: t.nav.accounts, icon: Wallet, also: ["/account/", "/account-group"] },
    { to: "/trading-journal", label: t.nav.journal, short: t.nav.journal, icon: BookOpen },
    { to: "/trading-sessions", label: t.nav.sessions, short: t.nav.sessions, icon: Timer, also: ["/session"] },
    { to: "/discipline-manager", label: t.nav.discipline, short: t.nav.discipline, icon: ListChecks },
    { to: "/trading-analytics", label: t.nav.stats, short: t.nav.stats, icon: BarChart3 },
    { to: "/my-analyses", label: t.nav.edgy, short: t.nav.edgyShort, icon: Sparkles, also: ["/playbook"] },
  ];
  // Barre mobile : 4 onglets + le bouton central, le reste passe dans l'en-tête.
  const byPath = (to: string) => nav.find(n => n.to === to)!;
  const tabs = ["/dashboard", "/trading-journal", "/trading-analytics", "/prop-firm-manager"].map(byPath);

  return (
    <div className="flex bg-background text-foreground min-h-screen">
      <aside className="hidden lg:flex w-60 shrink-0 flex-col border-r border-border bg-card h-screen sticky top-0" data-menu-open={menuOpen}>
        <div className="px-5 h-16 flex items-center gap-2">
          <Logo />
          <span className="text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{t.nav.liteBadge}</span>
        </div>
        <div className="px-3">
          <Button className="w-full lite-cta" onClick={openAddTrade}><Plus className="h-4 w-4 mr-1.5" />{t.nav.addTrade}</Button>
        </div>
        <nav className="px-3 mt-5 space-y-0.5 flex-1 overflow-y-auto">
          {nav.map(n => <SideLink key={n.to} {...n} />)}
        </nav>
        <div className="px-3 pb-2 space-y-0.5">
          {/* Rapports : bilan chiffré par période, hors de la navigation principale. */}
          <SideLink to="/reports" label={t.nav.reports} short={t.nav.reports} icon={FileText} />
          {/* Mêmes notifications que l'Advanced (cloche du rail), en ligne de sidebar. */}
          <NotificationsBell trigger="row" side="right" align="end" />
          <SideLink to="/account" label={t.nav.settings} short={t.nav.settings} icon={Settings} end />
        </div>
        <div className="px-2 pb-3 border-t border-border pt-2">
          <SidebarBottomSection isCollapsed={false} user={user} onMenuOpenChange={setMenuOpen} compactUtilities />
        </div>
      </aside>

      <div className="flex flex-col flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-30 h-14 flex items-center justify-between px-4 border-b border-border bg-background/90 backdrop-blur">
          <Logo />
          <div className="flex items-center gap-1">
            <NotificationsBell trigger="icon" side="bottom" align="end" />
            <NavLink to="/trading-sessions" className="w-9 h-9 grid place-items-center rounded-lg text-muted-foreground hover:bg-muted" aria-label={t.nav.sessions}><Timer size={18} /></NavLink>
            <NavLink to="/discipline-manager" className="w-9 h-9 grid place-items-center rounded-lg text-muted-foreground hover:bg-muted" aria-label={t.nav.discipline}><ListChecks size={18} /></NavLink>
            <NavLink to="/my-analyses" className="w-9 h-9 grid place-items-center rounded-lg text-muted-foreground hover:bg-muted" aria-label={t.nav.edgy}><Sparkles size={18} /></NavLink>
            <NavLink to="/trading-accounts" className="w-9 h-9 grid place-items-center rounded-lg text-muted-foreground hover:bg-muted" aria-label={t.nav.accounts}><Wallet size={18} /></NavLink>
            <NavLink to="/reports" className="w-9 h-9 grid place-items-center rounded-lg text-muted-foreground hover:bg-muted" aria-label={t.nav.reports}><FileText size={18} /></NavLink>
            <NavLink to="/account" end className="w-9 h-9 grid place-items-center rounded-lg text-muted-foreground hover:bg-muted" aria-label={t.nav.settings}><Settings size={18} /></NavLink>
          </div>
        </header>
        {banner}
        <main className="flex-1 w-full px-4 md:px-8 py-4 pb-24 lg:pb-8">
          {/* Clé = chemin : chaque changement de page rejoue le fondu d'entrée (Main.css, .lite-page). */}
          <div key={pathname} className="lite-page">{children}</div>
        </main>
      </div>

      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 border-t border-border bg-card/95 backdrop-blur grid grid-cols-5 h-16 pb-[env(safe-area-inset-bottom)]">
        {tabs.slice(0, 2).map(n => <TabLink key={n.to} {...n} />)}
        <button type="button" onClick={openAddTrade} className="grid place-items-center" aria-label={t.nav.addTrade}>
          <span className="w-11 h-11 rounded-2xl bg-primary text-primary-foreground grid place-items-center shadow-lg shadow-primary/20"><Plus size={22} /></span>
        </button>
        {tabs.slice(2, 4).map(n => <TabLink key={n.to} {...n} />)}
      </nav>

      <LiteIntroModal />
      <LiteOnboarding />
    </div>
  );
}

function SideLink({ to, label, icon: Icon, end, also }: NavItem) {
  const { pathname } = useLocation();
  const alsoActive = !!also?.some(p => p.endsWith("/") ? pathname.startsWith(p) : pathname === p || pathname.startsWith(`${p}/`));
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-xl px-3 h-10 text-sm transition-colors ${isActive || alsoActive ? "bg-primary/15 text-primary font-medium lite-nav-active" : "text-muted-foreground hover:text-foreground hover:bg-muted/60"}`
      }
    >
      {({ isActive }) => (<><Icon size={18} className={isActive || alsoActive ? "text-primary" : ""} />{label}</>)}
    </NavLink>
  );
}

function TabLink({ to, short, icon: Icon, end }: NavItem) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => `flex flex-col items-center justify-center gap-0.5 text-[11px] ${isActive ? "text-primary" : "text-muted-foreground"}`}>
      <Icon size={20} />
      {short}
    </NavLink>
  );
}
